import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from "../../../config";
import { isAuthenticated, AuthRequired } from './payment';

// ---------------------------------------------------------------------------
// ENGINEER PAYMENT (PAYROLL) MODULE (self-contained)
// Engineer views own payments (read-only).
// Admin / SuperEngineer edits allowances/deductions and marks as paid.
// Usage:  <EngineerPayment currentUser={{ id, name, role, token }} />
// ---------------------------------------------------------------------------

const api = axios.create({
  baseURL: BASE_URL,
});

// FIX: token was never attached — see the identical note in Advancepayment.jsx.
api.interceptors.request.use((config) => {
  try {
    const stored = JSON.parse(localStorage.getItem('NagpurProperties') || '{}');
    if (stored?.token) config.headers.Authorization = `Bearer ${stored.token}`;
  } catch {
    // no stored session — request goes out unauthenticated, backend will 401
  }
  return config;
});

// FIX: normalize role comparison instead of exact-match against a fixed-case
// array — see the identical note in Advancepayment.jsx.
const REVIEWER_ROLE_VARIANTS = ['ADMIN', 'SUPERENGINEER', 'SUPER_ENGINEER', 'SUPER ENGINEER'];
function isReviewerRole(role) {
  return REVIEWER_ROLE_VARIANTS.includes(String(role || '').toUpperCase());
}

const editEngineerPayment = (paymentId, editedBy, payload) =>
  api.patch(`/engineer/payment/${paymentId}`, payload, { params: { editedBy } }).then((r) => r.data);

const markPaymentAsPaid = (paymentId, paidBy) =>
  api.patch(`/engineer/payment/${paymentId}/paid`, null, { params: { paidBy } }).then((r) => r.data);

const getEngineerPayments = (params) =>
  api.get('/engineer/payments', { params }).then((r) => r.data);

// ---------------------------------------------------------------------------
// UI: Edit modal (Admin / SuperEngineer)
// ---------------------------------------------------------------------------
function PaymentEditModal({ payment, currentUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    petrolAllowance: payment.petrolAllowance ?? 0,
    lateDayDeduction: payment.lateDayDeduction ?? 0,
    advanceDeduction: payment.advanceDeduction ?? 0,
    editRemark: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.editRemark.trim()) {
      setError('Edit remark is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await editEngineerPayment(payment.paymentId, currentUser.name || currentUser.id, {
        petrolAllowance: Number(form.petrolAllowance),
        lateDayDeduction: Number(form.lateDayDeduction),
        advanceDeduction: Number(form.advanceDeduction),
        editRemark: form.editRemark,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Edit Payment #{payment.paymentId}</h3>
        <p style={styles.subtitle}>{payment.engineerName} · {payment.month}/{payment.year}</p>

        <label style={styles.label}>
          Petrol allowance
          <input type="number" name="petrolAllowance" value={form.petrolAllowance} onChange={handleChange} style={styles.input} />
        </label>
        <label style={styles.label}>
          Late day deduction
          <input type="number" name="lateDayDeduction" value={form.lateDayDeduction} onChange={handleChange} style={styles.input} />
        </label>
        <label style={styles.label}>
          Advance deduction
          <input type="number" name="advanceDeduction" value={form.advanceDeduction} onChange={handleChange} style={styles.input} />
        </label>
        <label style={styles.label}>
          Edit remark (required)
          <textarea name="editRemark" value={form.editRemark} onChange={handleChange} rows={2} style={styles.input} />
        </label>

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.modalActions}>
          <button onClick={onClose} disabled={saving} style={styles.btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={styles.btnPrimary}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root export
// ---------------------------------------------------------------------------
const now = new Date();

export default function EngineerPayment({ currentUser }) {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const isReviewer = isReviewerRole(currentUser?.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // FIX: this endpoint returns every engineer's payroll for the period —
      // fine for a reviewer, but a plain engineer must only ever see their
      // own row. Rather than trust the backend to filter (it may not), scope
      // the request with engineerId when the caller isn't a reviewer, and
      // additionally filter the result client-side as a second guard.
      const params = { month, year };
      if (!isReviewer) params.engineerId = currentUser.id;
      const result = await getEngineerPayments(params);
      const list = Array.isArray(result) ? result : [];
      setItems(isReviewer ? list : list.filter((p) => p.engineerId === currentUser.id));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [month, year, isReviewer, currentUser?.id]);

  useEffect(() => { load(); }, [load]);

  const handleMarkPaid = async (paymentId) => {
    setPayingId(paymentId);
    try {
      await markPaymentAsPaid(paymentId, currentUser.name || currentUser.id);
      load();
    } catch (err) {
      alert(err?.response?.data?.message || err.message);
    } finally {
      setPayingId(null);
    }
  };

  if (!isAuthenticated(currentUser)) return <AuthRequired />;

  return (
    <div>
      <div style={styles.filterRow}>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={styles.input}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>
          ))}
        </select>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={styles.input}>
          {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading && <p style={styles.muted}>Loading...</p>}
      {error && <p style={styles.error}>{error}</p>}
      {!loading && !error && !items.length && <p style={styles.muted}>No payments found for this period.</p>}

      {!loading && !error && items.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Engineer</th>
              <th style={styles.th}>Period</th>
              <th style={styles.th}>Basic</th>
              <th style={styles.th}>Deductions</th>
              <th style={styles.th}>Net Pay</th>
              <th style={styles.th}>Status</th>
              {isReviewer && <th style={styles.th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.paymentId}>
                <td style={styles.td}>{p.paymentId}</td>
                <td style={styles.td}>{p.engineerName}</td>
                <td style={styles.td}>{p.month}/{p.year}</td>
                <td style={styles.td}>₹{p.basicSalary}</td>
                <td style={styles.td}>₹{p.totalDeduction}</td>
                <td style={styles.td}>₹{p.netPay}{p.isEdited && <span style={styles.tag}>edited</span>}</td>
                <td style={styles.td}>{p.status}</td>
                {isReviewer && (
                  <td style={{ ...styles.td, display: 'flex', gap: 12 }}>
                    <button style={styles.linkBtn} onClick={() => setEditing(p)}>Edit</button>
                    {p.status !== 'PAID' && (
                      <button style={styles.linkBtn} disabled={payingId === p.paymentId} onClick={() => handleMarkPaid(p.paymentId)}>
                        {payingId === p.paymentId ? 'Marking...' : 'Mark Paid'}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <PaymentEditModal payment={editing} currentUser={currentUser} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}

const styles = {
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#444' },
  input: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontWeight: 400 },
  btnPrimary: { padding: '8px 16px', border: 'none', borderRadius: 6, background: '#2d5bd7', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', color: '#333', fontWeight: 600, cursor: 'pointer' },
  linkBtn: { background: 'none', border: 'none', color: '#2d5bd7', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13 },
  error: { color: '#c0392b', fontSize: 13 },
  muted: { color: '#777', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' },
  th: { padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', background: '#fafafa', fontSize: 13, fontWeight: 600, color: '#555' },
  td: { padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', fontSize: 13 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', width: 420, maxWidth: '92vw', borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflowY: 'auto' },
  subtitle: { color: '#777', fontSize: 13, margin: '-8px 0 4px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  tag: { marginLeft: 6, fontSize: 11, color: '#b9770e', background: '#fff4e5', padding: '2px 6px', borderRadius: 4 },
};